import React, { useState } from 'react';
import { X } from 'lucide-react';

interface UnsubscribeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, otherReasonText?: string) => void;
  isLoading?: boolean;
  currentPeriodEnd?: string | null;
}

const formatDate = (iso: string | null | undefined): string | null =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null;

const UNSUBSCRIBE_REASONS = [
  { value: "gathered_all_leads", label: "I've gathered all the leads I needed" },
  { value: "wrong_leads", label: "Not getting the leads I expected" },
  { value: "too_expensive", label: "Too expensive for my current needs" },
  { value: "not_using_enough", label: "Not using it often enough" },
  { value: "switching_tools", label: "Switching to another tool" },
  { value: "other", label: "Other reason" },
];

export const UnsubscribeModal: React.FC<UnsubscribeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  currentPeriodEnd,
}) => {
  const periodEndLabel = formatDate(currentPeriodEnd);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [otherReasonText, setOtherReasonText] = useState<string>('');
  const [error, setError] = useState<string>('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selectedReason) {
      setError('Please select a reason');
      return;
    }

    if (selectedReason === 'other' && !otherReasonText.trim()) {
      setError('Please tell us why you are unsubscribing');
      return;
    }

    setError('');
    const reasonToSend = selectedReason === 'other' 
      ? otherReasonText 
      : UNSUBSCRIBE_REASONS.find(r => r.value === selectedReason)?.label || selectedReason;
    onConfirm(reasonToSend, selectedReason === 'other' ? otherReasonText : undefined);
  };

  const handleClose = () => {
    if (!isLoading) {
      setSelectedReason('');
      setOtherReasonText('');
      setError('');
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl">
        <div className="px-6 pt-6 pb-5 sm:px-8 sm:pt-7 sm:pb-6">
          <div className="flex items-start justify-between mb-5">
            <h3 className="text-2xl font-semibold text-gray-900">
              We're sorry to see you go
            </h3>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none disabled:opacity-50 ml-4 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="mb-5 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-md">
            <p className="text-sm text-blue-900 font-medium">
              <strong>Important:</strong> Once you unsubscribe, you won't be charged again.{' '}
              {periodEndLabel ? (
                <>
                  You'll keep your current plan and quota until <strong>{periodEndLabel}</strong>, then you'll move to
                  the <strong>Discover</strong> plan.
                </>
              ) : (
                <>
                  You'll keep your current plan and quota until the end of your billing period, then you'll move to
                  the <strong>Discover</strong> plan.
                </>
              )}
            </p>
          </div>

          <p className="text-sm text-gray-600 mb-5">
            Before you unsubscribe, could you tell us why? This helps us improve WebLeads.
          </p>

          <div className="space-y-3">
            {UNSUBSCRIBE_REASONS.map((reason) => (
              <div key={reason.value} className="flex flex-col">
                <label className="flex items-start cursor-pointer group p-2 rounded-md hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="unsubscribe-reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => {
                      setSelectedReason(e.target.value);
                      setError('');
                    }}
                    disabled={isLoading}
                    className="mt-0.5 h-4 w-4 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 border-gray-300 cursor-pointer disabled:opacity-50 flex-shrink-0"
                  />
                  <span className="ml-3 text-sm text-gray-800 group-hover:text-gray-900 leading-snug">
                    {reason.label}
                  </span>
                </label>
                
                {selectedReason === 'other' && reason.value === 'other' && (
                  <textarea
                    value={otherReasonText}
                    onChange={(e) => {
                      setOtherReasonText(e.target.value);
                      setError('');
                    }}
                    disabled={isLoading}
                    placeholder="Please tell us more..."
                    rows={3}
                    maxLength={500}
                    className="mt-2 ml-7 block w-[calc(100%-1.75rem)] rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm disabled:opacity-50 resize-none"
                  />
                )}
              </div>
            ))}
          </div>

          {error && (
            <div className="mt-3 text-sm text-red-600">
              {error}
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse sm:px-8 gap-3 rounded-b-xl border-t border-gray-200">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className="inline-flex w-full justify-center rounded-lg border border-transparent bg-red-600 px-6 py-2.5 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Unsubscribing...' : 'Confirm Unsubscribe'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="mt-3 inline-flex w-full justify-center rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

